import { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ExpressDetailModal from './ExpressDetailModal';

// ── Decode Google encoded polyline ─────────────────────────
function decodePolyline(encoded) {
  if (!encoded) return [];
  let index = 0, lat = 0, lng = 0;
  const coordinates = [];

  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    lat += (result & 1) ? ~(result >> 1) : result >> 1;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    lng += (result & 1) ? ~(result >> 1) : result >> 1;
    coordinates.push([lat / 1e5, lng / 1e5]);
  }

  return coordinates;
}

// ── Custom marker icons ────────────────────────────────────
function createDeliveryIcon(status, hasDriver) {
  const colors = {
    pending: '#E6B43C',
    en_recojo: '#f97316',
    encamino: '#3b82f6',
    entregado: '#67A43C',
    rechazado: '#6b7280',
    cancelado: '#9ca3af',
    postergado: '#a855f7',
  };

  const color = status === 'pending' && !hasDriver
    ? '#ef4444'
    : (colors[status] || '#B1336E');

  return L.divIcon({
    className: '',
    html: `<div style="
      width:28px;
      height:28px;
      border-radius:50% 50% 50% 0;
      background:${color};
      border:2px solid white;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);
      transform:rotate(-45deg);
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

function createDriverIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:32px;
      height:32px;
      border-radius:50%;
      background:#B1336E;
      border:3px solid white;
      box-shadow:0 2px 8px rgba(177,51,110,0.5);
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:16px;
    ">🏍️</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

const STATUS_LABELS = {
  pending: 'Pendiente',
  en_recojo: 'En recojo',
  encamino: 'En camino',
  entregado: 'Entregado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  postergado: 'Postergado',
};

// Lima center
const LIMA_CENTER = [-12.046374, -77.042793];

export default function ExpressMapTab({
  user_id,
  deliveries,
  drivers,
  updateDelivery,
  selectedDate,
  loading,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [activePolyline, setPolyline] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [showDrivers, setShowDrivers] = useState(true);

  function handleSelectDelivery(delivery) {
    setSelectedId(delivery.id);
    setPolyline(delivery.route_polyline ? decodePolyline(delivery.route_polyline) : null);
  }

  function handleCloseDetail() {
    setSelectedId(null);
    setPolyline(null);
  }

  // ── Client-side filters (data already date-scoped by dashboard) ──
  const visible = deliveries.filter((d) => {
    if (filterStatus && d.status !== filterStatus) return false;
    if (filterVehicle && d.vehicle_type !== filterVehicle) return false;
    if (!d.pickup_lat || !d.dropoff_lat) return false;
    return true;
  });

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-3 items-center">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={filterVehicle}
          onChange={(e) => setFilterVehicle(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
        >
          <option value="">Todos los vehículos</option>
          <option value="bike">🚴 Moto/Bici</option>
          <option value="car">🚗 Auto</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showDrivers}
            onChange={(e) => setShowDrivers(e.target.checked)}
            className="accent-brand-primary"
          />
          Ver conductores
        </label>

        <span className="ml-auto text-xs text-gray-400">
          {loading
            ? 'Cargando...'
            : `${visible.length} entrega${visible.length !== 1 ? 's' : ''} — ${selectedDate}`}
        </span>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {[
          { color: '#ef4444', label: 'Sin conductor' },
          { color: '#E6B43C', label: 'Pendiente' },
          { color: '#f97316', label: 'En recojo' },
          { color: '#3b82f6', label: 'En camino' },
          { color: '#67A43C', label: 'Entregado' },
          { color: '#a855f7', label: 'Postergado' },
          { color: '#9ca3af', label: 'Cancelado/Rechazado' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: color }} />
            <span className="text-gray-500">{label}</span>
          </div>
        ))}
      </div>

      {/* Map — z-index 0 keeps Leaflet's internal layers below modals (z-50) */}
      <div
        className="rounded-xl overflow-hidden border border-gray-200"
        style={{ height: '520px', zIndex: 0, position: 'relative' }}
      >
        {loading ? (
          <div className="h-full flex items-center justify-center bg-gray-50 text-gray-400 text-sm animate-pulse">
            Cargando entregas del mapa...
          </div>
        ) : (
          <MapContainer
            center={LIMA_CENTER}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Delivery markers */}
            {visible.map((d) => (
              <Marker
                key={d.id}
                position={[d.pickup_lat, d.pickup_lng]}
                icon={createDeliveryIcon(d.status, !!d.driver)}
                eventHandlers={{ click: () => handleSelectDelivery(d) }}
              >
                <Popup>
                  <div className="text-xs space-y-1 min-w-[180px]">
                    <div className="font-bold text-gray-800">
                      #{d.id} — {STATUS_LABELS[d.status] || d.status}
                    </div>
                    <div className="text-gray-600">{d.recipient_name}</div>
                    <div className="text-gray-500 truncate">{d.pickup_address}</div>
                    <div className="text-gray-500 truncate">→ {d.dropoff_address}</div>
                    <div className="text-gray-600 font-medium">
                      S/ {Number(d.total_price).toFixed(2)} · {d.distance_km} km
                    </div>
                    {d.driver ? (
                      <div className="text-brand-green">🏍 {d.driver.name}</div>
                    ) : (
                      <div className="text-red-500 font-medium">Sin conductor</div>
                    )}
                    <button
                      onClick={() => handleSelectDelivery(d)}
                      className="mt-1 w-full bg-brand-primary text-white text-xs py-1 rounded font-medium"
                    >
                      Ver detalle
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Route polyline for selected delivery */}
            {activePolyline && activePolyline.length > 0 && (
              <Polyline
                positions={activePolyline}
                pathOptions={{
                  color: '#B1336E',
                  weight: 4,
                  opacity: 0.8,
                  dashArray: '8 4',
                }}
              />
            )}

            {/* Driver markers — always current, not date filtered */}
            {showDrivers &&
              drivers
                .filter((d) => d.latitude && d.longitude)
                .map((drv) => (
                  <Marker
                    key={`driver-${drv.user_id}`}
                    position={[drv.latitude, drv.longitude]}
                    icon={createDriverIcon()}
                  >
                    <Popup>
                      <div className="text-xs space-y-1">
                        <div className="font-bold text-brand-primary">{drv.username}</div>
                        <div className="text-gray-500">{drv.phone_number}</div>
                        <div
                          className={
                            drv.active_express_count > 0 ? 'text-yellow-600' : 'text-green-600'
                          }
                        >
                          {drv.active_express_count > 0
                            ? `${drv.active_express_count} entrega(s) activa(s)`
                            : 'Disponible'}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
          </MapContainer>
        )}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <ExpressDetailModal
          deliveryId={selectedId}
          drivers={drivers}
          user_id={user_id}
          onClose={handleCloseDetail}
          onUpdate={updateDelivery}
        />
      )}
    </div>
  );
}